<!--
  a11y: like every overlay slide-up the audit asked us to (a) pull initial focus
  to the back/close chevron when the dialog opens and (b) keep keyboard/SR Tab
  navigation locked inside the dialog. The outer focus trap is set up by the
  shared OverlayComponent (ConfigurableFocusTrap on the cdk-overlay element +
  focusInitialElementWhenReady). For (a) we just need to flag the header back
  button via focusInitialOnHeaderBack so route-planner-header forwards
  cdkFocusInitial onto its inner native button.
-->
<design-slide-up-card
  #card
  [isCardOpen]="!!isCardOpen"
  class="filters-form overflow-x-hidden"
  [class]="direction"
  [title]="favoritesManagementText"
  [roleDialog]="true"
  [focusInitialOnHeaderBack]="true"
  [icon]="iconName"
  (onHeaderButtonClick)="closeCard(false)"
  [displayBackground]="true"
  >
  <div class="h-full flex flex-col overflow-hidden">
    <div class="flex flex-col gap-2 px-[0.8125rem] overflow-y-auto flex-1">
      <ng-container>
        <h3 class="text-base">{{ favoriteStationsText }}</h3>
        @if (
          !!storedFavorites?.stations?.stations?.length) {
          @for (favoriteStation of storedFavorites?.stations?.stations; track favoriteStation) {
            <app-favorite-station
              [station]="favoriteStation"
              [showActionButtons]="true"
              [editAriaLabel]="editFavoriteAriaLabel"
              [deleteAriaLabel]="deleteFavoriteAriaLabel"
              (onDeleteButtonClick)="openDeleteFavoritePopup(favoriteStation)"
              (onEditButtonClick)="openEditFavoritePopup(favoriteStation)"
              (onPickFavoriteClick)="pickFavoriteStation(favoriteStation)"
              />
              <mat-divider />
            }
          } @else {
            <!--
              a11y: empty-state used to be a clickable <div> (not focusable, not
              announced as a button). Replaced with a native <button> with
              aria-haspopup="dialog" so SR users get "...button, opens dialog".
            -->
            <button
              type="button"
              class="flex gap-2 items-center cursor-pointer bg-transparent border-0 p-0 text-start w-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0C3058]"
              [class]="direction"
              aria-haspopup="dialog"
              (click)="openEditFavoritePopup()">
              <img
                src="assets/images/route-planning/search-stations/station_empty_state.svg"
                alt=""
                aria-hidden="true"
                />
                <div class="flex flex-col gap-[0.125rem]">
                  <span class="text-[#3D5979] font-normal text-sm">{{ didntAddFavoriteStationsTextPart1 }}</span>
                  <span class="text-[#3D5979] font-normal text-sm">{{ didntAddFavoriteStationsTextPart2 }}</span>
                </div>
              </button>
            }
          </ng-container>

          <ng-container>
            <h3 class="text-base">{{ favoriteLinesText }}</h3>
            @if (
              !!storedFavorites?.lines?.routes?.length) {
              @for (favoriteLine of storedFavorites?.lines?.routes; track favoriteLine) {
                <app-favorite-line
                  [line]="favoriteLine"
                  [showActionButtons]="true"
                  [editAriaLabel]="editFavoriteAriaLabel"
                  [deleteAriaLabel]="deleteFavoriteAriaLabel"
                  (onDeleteButtonClick)="openDeleteFavoritePopup(favoriteLine)"
                  (onEditButtonClick)="openEditFavoritePopup(favoriteLine)"
                  (onPickFavoriteClick)="pickFavoriteLine(favoriteLine)"
                  />
                  <mat-divider />
                }
              } @else {
                <!-- a11y: same rationale as the station empty-state above. -->
                <button
                  type="button"
                  class="flex gap-2 items-center cursor-pointer bg-transparent border-0 p-0 text-start w-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0C3058]"
                  [class]="direction"
                  aria-haspopup="dialog"
                  (click)="openEditFavoritePopup()">
                  <img
                    src="assets/images/route-planning/search-line/line_empty_state.svg"
                    alt=""
                    aria-hidden="true"
                    />
                    <div class="flex flex-col gap-[0.125rem]">
                      <span class="text-[#3D5979] font-normal text-sm">{{ didntAddFavoriteLinesTextPart1 }}</span>
                      <span class="text-[#3D5979] font-normal text-sm">{{ didntAddFavoriteLinesTextPart2 }}</span>
                    </div>
                  </button>
                }
              </ng-container>

              <ng-container>
                <h3 class="text-base">{{ favoriteRoutesText }}</h3>
                @if (
                  !!storedFavorites?.routes?.length) {
                  @for (favoriteRoute of storedFavorites?.routes; track favoriteRoute) {
                    <app-favorite-route
                      [route]="favoriteRoute"
                      [showActionButtons]="true"
                      [editAriaLabel]="editFavoriteAriaLabel"
                      [deleteAriaLabel]="deleteFavoriteAriaLabel"
                      (onDeleteButtonClick)="openDeleteFavoritePopup(favoriteRoute)"
                      (onEditButtonClick)="openEditFavoritePopup(favoriteRoute)"
                      (onPickFavoriteClick)="pickFavoriteRoute(favoriteRoute)"
                      />
                      <mat-divider />
                    }
                  } @else {
                    <!-- a11y: same rationale as the station empty-state above. -->
                    <button
                      type="button"
                      class="flex gap-2 items-center cursor-pointer bg-transparent border-0 p-0 text-start w-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0C3058]"
                      [class]="direction"
                      aria-haspopup="dialog"
                      (click)="openEditFavoritePopup()">
                      <img
                        src="assets/images/route-planning/source-destination/route_empty_state.svg"
                        alt=""
                        aria-hidden="true"
                        />
                        <div class="flex flex-col gap-[0.125rem]">
                          <span class="text-[#3D5979] font-normal text-sm">{{ didntAddFavoriteRoutesTextPart1 }}</span>
                          <span class="text-[#3D5979] font-normal text-sm">{{ didntAddFavoriteRoutesTextPart2 }}</span>
                        </div>
                      </button>
                    }
                  </ng-container>

                  <ng-container>
                    <h3 class="text-base">{{ favoriteAddressesText }}</h3>
                    <!--
                      a11y: in "manage favorites", clicking home/work always opens
                      an edit/set dialog regardless of whether the favorite is set,
                      so the rows always expose aria-haspopup="dialog".
                    -->
                    <app-favorite-locations
                      [favoriteAddresses]="storedFavorites?.addresses"
                      [alwaysOpensPopup]="true"
                      (onHomeClick)="handleHomeClick($event)"
                      (onWorkClick)="handleWorkAddress($event)"
                      />
                    </ng-container>
                  </div>

                  <div class="pb-3 pt-2 flex flex-col gap-2 bg-white px-[0.8125rem] flex-shrink-0">
                    <mot-button
                      buttonClass="!bg-primary w-full !text-white !py-6 !rounded-lg !align-middle"
                      (onClick)="openEditFavoritePopup()" ariaHaspopup="dialog"
                      [text]="newFavoriteText"
                    ></mot-button>
                  </div>
                </div>
              </design-slide-up-card>
